import type { CustomDetector } from "i18next-browser-languagedetector";

const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  TJ: "tg",
  RU: "ru",
  UZ: "uz",
};

const SESSION_KEY = "geo_detected_lang";
const SUPPORTED_LANGS = ["en", "ru", "uz", "tg"];

/**
 * Parse Cloudflare /cdn-cgi/trace response to extract country code.
 * Response format is key=value lines, e.g. "loc=TJ"
 */
function parseCountryFromTrace(text: string): string | undefined {
  const match = text.match(/^loc=([A-Z]{2})$/m);
  return match?.[1];
}

/**
 * Fetch visitor's country from Cloudflare's free geolocation endpoint.
 * Returns a mapped language code or undefined.
 */
async function fetchGeoLanguage(): Promise<string | undefined> {
  try {
    const response = await fetch("/cdn-cgi/trace", { cache: "no-store" });
    if (!response.ok) return undefined;
    const text = await response.text();
    const country = parseCountryFromTrace(text);
    if (!country) return undefined;
    const lang = COUNTRY_TO_LANGUAGE[country];
    return lang && SUPPORTED_LANGS.includes(lang) ? lang : undefined;
  } catch {
    return undefined;
  }
}

/**
 * i18next custom detector plugin for geo-based language detection.
 * Synchronously checks sessionStorage cache; the actual async fetch
 * is handled by detectAndApplyGeoLanguage() after i18n.init().
 */
export const geoipDetector: CustomDetector = {
  name: "geoipDetector",
  lookup(): string | undefined {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached && SUPPORTED_LANGS.includes(cached)) {
      return cached;
    }
    return undefined;
  },
  cacheUserLanguage() {
    // No-op: we manage caching ourselves via sessionStorage
  },
};

/**
 * Async post-init function. Call after i18n.init() to handle the
 * async geolocation fetch. Only runs when the user has no saved
 * language preference in localStorage.
 */
export async function detectAndApplyGeoLanguage(
  i18n: { language: string; changeLanguage: (lng: string) => Promise<unknown> },
): Promise<void> {
  // If the user already made an explicit choice, don't override
  if (localStorage.getItem("i18nextLng")) return;

  // If we already have a cached geo result, it was applied synchronously
  if (sessionStorage.getItem(SESSION_KEY)) return;

  const lang = await fetchGeoLanguage();
  if (!lang) return;

  sessionStorage.setItem(SESSION_KEY, lang);

  // Only change if it differs from the current language
  if (i18n.language !== lang) {
    await i18n.changeLanguage(lang);
  }
}
